import {
	GET_LIST,
	GET_ONE,
	GET_MANY,
	GET_MANY_REFERENCE,
	CREATE,
	UPDATE,
	UPDATE_MANY,
	DELETE,
	DELETE_MANY,
} from 'react-admin';
import Parse from 'parse';

const getHint = (modelHint, resource, key) => {
	if (modelHint[resource]) return modelHint[resource].find((hint) => hint.key === key);
	return undefined;
};

const getKeyType = (modelHint, resource, key) => {
	const hint = getHint(modelHint, resource, key);
	return (hint && hint.type);
};

const isKeyFile = (modelHint, resource, key) => (
	getKeyType(modelHint, resource, key) === 'file'
);

const isKeyRelation = (modelHint, resource, key) => (
	getKeyType(modelHint, resource, key) === 'relation'
);

/* eslint-disable no-param-reassign */
const pointerizeData = (data, modelHint, resource) => {
	Object.keys(data).forEach((key) => {
		if (modelHint[resource]) {
			// eslint-disable-next-line no-shadow
			const hint = modelHint[resource].find((hint) => hint.key === key);
			if (hint) {
				switch (hint.type) {
					case 'pointer':
						if (data[key].id) {
							const HintClass = hint.className === '_User' ? Parse.User : Parse.Object.extend(hint.className);
							const hintObject = new HintClass();
							hintObject.id = data[key].id;
							data[key] = hintObject.toPointer();
						} else { data[key] = null; }
						break;
					case 'date':
						if ((typeof (data[key])) === 'string') { data[key] = new Date(data[key]); }
						break;
					default:
						break;
				}
			}
		}
	});
};

const processFiles = async (data, targetParseObject) => {
	const filesToSave = [];

	Object.keys(data).forEach((key) => {
		if (data[key]) {
			if (data[key].rawFile && data[key].rawFile instanceof File) {
				const parseFile = new Parse.File(data[key].rawFile.name, data[key].rawFile);
				filesToSave.push({ fileObject: parseFile, fileKey: key });
			} else if (targetParseObject.get(key) && targetParseObject.get(key) instanceof Parse.File) {
				data[key] = targetParseObject.get(key);
			}
		}
	});
	await Promise.all(filesToSave.map((file) => file.fileObject.save()));
	filesToSave.forEach((file) => {
		targetParseObject.set(file.fileKey, file.fileObject);
		data[file.fileKey] = file.fileObject;
	});
};
/* eslint-enable no-param-reassign */

const parseObjectToJS = (targetParseObject, modelHint, resource) => {
	const data = { id: targetParseObject.id, ...targetParseObject.attributes };
	const relationQueries = [];
	Object.keys(data).forEach((key) => {
		if (isKeyFile(modelHint, resource, key)) {
			if (data[key] instanceof Parse.File) {
				data[key] = {
					src: data[key].url(),
					title: data[key].name(),
				};
			} else {
				delete data[key];
			}
		}
		if (isKeyRelation(modelHint, resource, key)) {
			const relation = targetParseObject.relation(key);
			relationQueries.push(relation.query().find()
				.then((results) => { data[key] = results.map((result) => result.id); }));
		}
		if (data[key] instanceof Parse.Object) {
			data[key] = {
				id: data[key].id,
			};
		}
	});
	return Promise.all(relationQueries).then(() => data);
};

const createObjectWithId = (keyHint, id) => {
	const HintClass = Parse.Object.extend(keyHint.className);
	const hintObject = new HintClass();
	hintObject.id = id;
	return hintObject;
};

const modelHint = {
	Business: [
		{
			key: 'category',
			type: 'pointer',
			className: 'Category',
		},
		{
			key: 'logoFile',
			type: 'file',
			className: 'Category',
		},
		{
			key: 'mainVersion',
			type: 'pointer',
			className: 'Business',
		},
	],
	TradeUnionAdmin: [
		{
			key: 'tradeUnion',
			type: 'pointer',
			className: 'TradeUnion',
		},
		{
			key: 'parseUser',
			type: 'pointer',
			className: 'User',
		},
	],
	UserProfile: [
		{
			key: 'user',
			type: 'pointer',
			className: 'User',
		},
		{
			key: 'dateOfBirth',
			type: 'date',
		},
		{
			key: 'updatedAt',
			type: 'date',
		},
		{
			key: 'tradeUnion',
			type: 'pointer',
			className: 'TradeUnion',
		},
	],
	Promotion: [
		{
			key: 'business',
			type: 'pointer',
			className: 'Business',
		},
		{
			key: 'startDate',
			type: 'date',
		},
		{
			key: 'finishDate',
			type: 'date',
		},
		{
			key: 'startShowDate',
			type: 'date',
		},
	],
	PushMessage: [
		{
			key: 'business',
			type: 'pointer',
			className: 'Business',
		},
		{
			key: 'sendAt',
			type: 'date',
		},
	],
	Review: [
		{
			key: 'business',
			type: 'pointer',
			className: 'Business',
		},
		{
			key: 'user',
			type: 'pointer',
			className: 'User',
		},
		{
			key: 'dateOfVisit',
			type: 'date',
		},
		{
			key: 'dateWrittenRating',
			type: 'date',
		},
		{
			key: 'updatedAt',
			type: 'date',
		},
		{
			key: 'createdAt',
			type: 'date',
		},
	],
	Feedback: [
		{
			key: 'user',
			type: 'pointer',
			className: 'User',
		},
		{
			key: 'admin',
			type: 'pointer',
			className: 'User',
		},
	],
	Downloadable: [
		{
			key: 'content',
			type: 'file',
		},
	],
	TradeUnionNews: [
		{
			key: 'tradeUnion',
			type: 'pointer',
			className: 'TradeUnion',
		},
		{
			key: 'displayDate',
			type: 'date',
		},
		{
			key: 'documents',
			type: 'relation',
			className: 'Downloadable',
		},
	],
	AppEvent: [
		{
			key: 'dateTime',
			type: 'date',
		},
	],
	BusinessAdmin: [
		{
			key: 'business',
			type: 'pointer',
			className: 'Business',
		},
		{
			key: 'parseUser',
			type: 'pointer',
			className: 'User',
		},
	],
};

const buildFilter = (query, filterValues, path = '') => {
	if (filterValues instanceof Object) {
		Object.keys(filterValues).forEach(
			(k) => {
				if (k === '$gt') {
					return query.greaterThan(path, filterValues[k]);
				}
				if (k === '$gte') {
					return query.greaterThanOrEqualTo(path, filterValues[k]);
				}
				if (k === '$lt') {
					return query.lessThan(path, filterValues[k]);
				}
				if (k === '$lte') {
					return query.lessThanOrEqualTo(path, filterValues[k]);
				}
				if (k === '$ne') {
					return query.notEqualTo(path, filterValues[k]);
				}
				if (k === '$eq') {
					return query.equalTo(path, filterValues[k]);
				}
				if (k === '$exists') {
					if (filterValues[k] === true) {
						return query.exists(path);
					}
					if (filterValues[k] === false) {
						return query.doesNotExist(path);
					}
					throw new Error(`Invalid value for $exists filter criteria ${filterValues[k]}`);
				}
				if (k === '$or' && filterValues[k] instanceof Array) {
					return query._orQuery(
						filterValues[k].map((v) => buildFilter(new Parse.Query(query.className), v)),
					);
				}
				const keyHint = getHint(modelHint, query.className, k);
				if (keyHint && keyHint.type === 'pointer') {
					if (Object.hasOwn(filterValues[k], 'id')) {
						if (filterValues[k].id) {
							const hintObject = createObjectWithId(keyHint, filterValues[k].id);
							return query.equalTo(k, hintObject);
						}
						return query;
					}
				}
				return buildFilter(query, filterValues[k], `${path}${path ? '.' : ''}${k}`);
			},
		);
		return query;
	}
	return query.contains(path, filterValues);
};

/**
 * Выполняет поиск и возвращает список объектов
 * @param {string} resource - название ресурса react-admin
 * @param {object} params - параметры запроса
 * @param {object} params.sort - параметры сортировки результатов запроса
 * @param {object} params.pagination - параметры пагинации результатов запроса
 * @param {object} params.filter - параметры фильтрации результатов запроса
 * @return {Promise<{ data: object[], total: int}>}
 */
const getList = (resource, params = {}) => {
	// { pagination: { page: {int} , perPage: {int} },
	// sort: { field: {string}, order: {string} }, filter: {Object}, meta: {Object} }
	const {
		pagination: { page = 1, perPage = 20 } = {},
		sort: { field, order } = {},
		filter,
	} = params;

	const ResourceObj = Parse.Object.extend(resource);
	const query = new Parse.Query(ResourceObj);

	query.limit(perPage);
	query.skip((page - 1) * perPage);

	if (order === 'DESC') {
		query.descending(field);
	}	else if (order === 'ASC') {
		query.ascending(field);
	}

	buildFilter(query, filter);

	return query.count()
		.then((total) => query.find()
			.then((results) => Promise.all(results.map((o) => (parseObjectToJS(o, modelHint, resource)))))
			.then((results) => ({
				total,
				data: results,
			})));
};

/**
 * Возвращает один объект с конкретным идентификатором
 * @param {string} resource - название ресурса react-admin
 * @param {object} params - параметры
 * @param {*} params.id - идентификатор
 * @returns {Promise<{data: object}>}
 */
const getOne = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	const query = new Parse.Query(ResourceObj);
	return query.get(params.id)
		.then((result) => parseObjectToJS(result, modelHint, resource))
		.then((result) => ({
			data: result,
		}));
};

/**
 * Возвращает несколько объектов с конкретными идентификаторми
 * @param {string} resource - название ресурса react-admin
 * @param {object} params - параметры
 * @param {*[]} params.ids - идентификатор
 * @return {Promise<{data: object[]}>}
 */
const getMany = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	const queries = params.ids.map((id) => (new Parse.Query(ResourceObj)).get(id));
	return Promise.all(queries)
		.then((results) => (
			Promise.all(results.map((o) => (parseObjectToJS(o, modelHint, resource))))))
		.then((results) => ({
			data: results,
		}));
};

const getManyReference = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	const query = new Parse.Query(ResourceObj);
	const { page, perPage } = params.pagination;
	const { field, order } = params.sort;
	const hint = getHint(modelHint, resource, params.target);
	if (hint && hint.type === 'pointer') {
		const hintObject = createObjectWithId(hint, params.id);
		query.equalTo(params.target, hintObject);
	} else { query.equalTo(params.target, params.id); }
	query.limit(perPage);
	query.skip((page - 1) * perPage);
	if (order === 'DESC') query.descending(field);
	else if (order === 'ASC') query.ascending(field);

	return query.count()
		.then((total) => query.find()
			.then((results) => Promise.all(results.map((o) => (parseObjectToJS(o, modelHint, resource)))))
			.then((results) => ({
				total,
				data: results,
			})));
};

/**
 * Создает объект
 * @param {string} resource - название ресурса react-admin
 * @param {object} params - параметры
 * @param {object} params.data - данные объекта для создания
 * @return {Promise<{data: object}>}
 */
const create = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	const resourceObject = new ResourceObj();
	pointerizeData(params.data, modelHint, resource);

	return processFiles(params.data, resourceObject)
		.then(() => resourceObject.save(params.data))
		.then(() => parseObjectToJS(resourceObject, modelHint, resource))
		.then((data) => ({ data }));
};

const processRelations = (resource, data, resourceObject) => {
	const retVal = { ...data };
	const hints = modelHint[resource];
	if (hints) {
		return Promise.all(hints
			.filter((h) => h.type === 'relation')
			.map((h) => {
				if (!data[h.key]) {
					retVal[h.key] = [];
				}
				return resourceObject.relation(h.key).query().find()
					.then((results) => {
						resourceObject.relation(h.key).remove(
							results.filter((result) => data[h.key].indexOf(result.id) === -1),
						);
						resourceObject.relation(h.key).add(
							data[h.key]
								.filter((relKey) => results.map((r) => r.id).indexOf(relKey) === -1)
								.map((relRey) => createObjectWithId(h, relRey)),
						);
					})
					.then(() => {
						delete retVal[h.key];
					});
			}))
			.then(() => retVal);
	}
	return Promise.resolve(retVal);
};

/**
 * Обновляет объект
 * @param {string} resource - название ресурса react-admin
 * @param {object} params - параметры
 * @param {object} params.data - данные объекта для обновления
 * @param {*} params.id - идентификатор объекта для обновления
 * @return {Promise<{data: object}>}
 */
const update = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	const query = new Parse.Query(ResourceObj);
	return query.get(params.id)
		.then((resourceObject) => {
			pointerizeData(params.data, modelHint, resource);
			return processFiles(params.data, resourceObject)
				.then(() => {
					const keys = Object.keys(params.data)
						.filter(
							(o) => (!(o === 'id' || o === 'createdAt' || o === 'updatedAt' || o === 'ACL')),
						);
					const data = keys.reduce((r, f) => {
						r[f] = params.data[f]; // eslint-disable-line no-param-reassign
						return r;
					}, {});
					return Promise.resolve()
						.then(() => processRelations(resource, data, resourceObject))
						.then((relationProcessedData) => resourceObject.save(relationProcessedData))
						.then(() => parseObjectToJS(resourceObject, modelHint, resource))
						.then((result) => ({ data: result }));
				});
		});
};

const updateMany = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	return Promise.all(
		params.ids.map((id) => (new Parse.Query(ResourceObj)).get(id)),
	)
		.then((qs) => {
			qs.map((q) => q.save(params.data));
			return { data: params.ids };
		});
};

const deleteObject = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	const query = new Parse.Query(ResourceObj);
	return query.get(params.id)
		.then((obj) => {
			const data = { data: { id: obj.id, ...obj.attributes } };
			return obj.destroy()
				.then(() => {
					return data;
				});
		});
};

const deleteMany = (resource, params) => {
	const ResourceObj = Parse.Object.extend(resource);
	return Promise.all(
		params.ids.map((id) => (new Parse.Query(ResourceObj)).get(id)),
	)
		.then((qs) => {
			return Promise.all(qs.map((q) => q.destroy()))
				.then(() => ({ data: params.ids }));
		});
};
// const newProvider = ({	URL, APP_ID, JAVASCRIPT_KEY }) => {
// 	if (Parse.applicationId == null || Parse.javaScriptKey == null) {
// 		Parse.initialize(APP_ID, JAVASCRIPT_KEY);
// 		Parse.serverURL = URL;
// 	}
// 	return {
// 		getList,
// 		getOne,
// 		getMany,
// 		getManyReference,
// 		create,
// 		update,
// 		updateMany,
// 		delete: deleteObject,
// 		deleteMany,
// 	};
// };

export default ({	URL, APP_ID, JAVASCRIPT_KEY }) => {
	if (Parse.applicationId == null || Parse.javaScriptKey == null) {
		Parse.initialize(APP_ID, JAVASCRIPT_KEY);
		Parse.serverURL = URL;
	}
	const provider = async (type, resource, params) => {
		switch (type) {
			case GET_LIST: {
				return getList(resource, params);
			}
			case GET_ONE: {
				return getOne(resource, params);
			}
			case GET_MANY: {
				return getMany(resource, params);
			}
			case GET_MANY_REFERENCE: {
				return getManyReference(resource, params);
			}
			case CREATE: {
				return create(resource, params);
			}
			case UPDATE: {
				return update(resource, params);
			}
			case UPDATE_MANY: {
				return updateMany(resource, params);
			}
			case DELETE: {
				return deleteObject(resource, params);
			}
			case DELETE_MANY: {
				return deleteMany(resource, params);
			}
			default:
				throw Error('Unhandled type');
		}
	};
	provider.modelHint = modelHint;
	provider.pointerizeData = pointerizeData;
	return provider;
};

